import { Loader2 } from "lucide-react"
import type { PropsWithChildren } from "react"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { useNavigate } from "react-router-dom"

import { api } from "@/api"
import {
  ENDPOINTS,
  FRONTEND_ROUTES,
  KEY_VALUES,
} from "@/constants"
import { getBrowserQueryClient } from "@/providers"
import { login } from "@/services"
import type { PermissionsResponseType } from "@/types/permissions"
import {
  NavigationHelper,
  StorageHelper,
  StringHelper,
} from "@/utils"
export const AuthContext = createContext<
  AuthContextProps | undefined
>(undefined)
export const AuthContextProvider = ({
  children,
}: PropsWithChildren) => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [authData, setAuthData] = useState<
    AuthDataType | undefined
  >(() => {
    const userData = StorageHelper.getItem(
      KEY_VALUES.AUTH_LOCAL_STORAGE_KEY
    )
    return userData ? JSON.parse(userData) : undefined
  })
  const [forgotData, setForgotData] = useState<{
    email: string
  } | null>(null)
  const [selectedWhiteLabel, setSelectedWhiteLabel] =
    useState<WhitelabelType | null | undefined>(null)
  const {
    X_ACCESS_TOKEN_KEY,
    AUTH_LOCAL_STORAGE_KEY_FORGOT,
    AUTH_LOCAL_STORAGE_KEY,
    AUTH_LOCAL_STORAGE_KEY_WHITELABEL_UUID,
  } = KEY_VALUES
  const { stringify } = StringHelper
  const handleAuthData = useCallback(
    (data?: LoginResponseType) => {
      if (data) {
        StorageHelper.setItem(
          X_ACCESS_TOKEN_KEY,
          data.accessToken
        )
        StorageHelper.setItem(
          AUTH_LOCAL_STORAGE_KEY,
          stringify(data)
        )
      } else {
        StorageHelper.removeItem(X_ACCESS_TOKEN_KEY)
        StorageHelper.removeItem(AUTH_LOCAL_STORAGE_KEY)
      }
      setAuthData(data)
    },
    [
      X_ACCESS_TOKEN_KEY,
      AUTH_LOCAL_STORAGE_KEY,
      setAuthData,
      stringify,
    ]
  )
  const handleloginWithProvider = async (
    auth: LoginResponseType
  ) => {
    handleAuthData(auth)
    const targetRoute = await getRedirectRoute()
    navigate(targetRoute)
  }
  const loginWithProvider = async (
    authentication: string
  ) => {
    setIsLoading(true)
    try {
      const decodedAuth = JSON.parse(atob(authentication))
      if (decodedAuth?.accessToken) {
        const transformedJson = {
          accessToken: decodedAuth?.accessToken,
          user: {
            merchantId: decodedAuth?.user.merchantId,
            userId: decodedAuth?.user.userId,
            fullName: decodedAuth?.user.fullName,
          },
        }
        handleloginWithProvider(transformedJson)
      }
    } catch (error) {
      console.error(
        "Error processing authentication",
        error
      )
    } finally {
      setIsLoading(false)
    }
  }
  const getRedirectRoute = async (): Promise<string> => {
    try {
      const { data } =
        await api.get<PermissionsResponseType>(
          ENDPOINTS.PERMISSIONS
        )
      return NavigationHelper.getFirstAccessibleRoute(
        data.permissionGroups,
        data.superAdmin
      )
    } catch {
      return FRONTEND_ROUTES.DASHBOARD.href
    }
  }
  const fetchLogin = async (formData: formDataType) => {
    try {
      const response = await login(formData)
      handleAuthData(response)
      const targetRoute = await getRedirectRoute()
      navigate(targetRoute)
      return { success: true, isConnectionError: false }
    } catch (error) {
      setAuthData(undefined)
      const apiError = error as ErrorType

      if (apiError?.isFirstLogin) {
        handleSetForgotData(formData.email)
        navigate(FRONTEND_ROUTES.FIRST_LOGIN.href)
        return {
          success: false,
          isConnectionError: false,
          isFirstLogin: true,
        }
      }

      return {
        success: false,
        isConnectionError:
          apiError?.isConnectionError ?? false,
      }
    }
  }
  const logout = async () => {
    const {
      X_ACCESS_TOKEN_KEY,
      AUTH_LOCAL_STORAGE_KEY_FORGOT,
      AUTH_LOCAL_STORAGE_KEY_WHITELABEL_UUID,
    } = KEY_VALUES

    // Primeiro: Limpar cache do QueryClient - cancelar queries ativas ANTES de remover token
    const queryClient = getBrowserQueryClient()
    if (queryClient) {
      // Cancelar todas as queries em andamento
      await queryClient.cancelQueries()
      // Limpar todo o cache de forma síncrona
      queryClient.clear()
    }

    // Limpar header da API ANTES de qualquer outra operação
    delete api.defaults.headers[X_ACCESS_TOKEN_KEY]

    // Limpar dados de autenticação
    handleAuthData()

    // Limpar estados do contexto
    setSelectedWhiteLabel(null)
    setForgotData(null)

    // Limpar localStorage
    StorageHelper.removeItem(AUTH_LOCAL_STORAGE_KEY_FORGOT)
    StorageHelper.removeItem(
      AUTH_LOCAL_STORAGE_KEY_WHITELABEL_UUID
    )

    // Navegar para login
    navigate(FRONTEND_ROUTES.LOGIN.href)
  }
  const handleUpdateAuthData = useCallback(
    async (providerAuthData?: LoginResponseType) => {
      if (!providerAuthData?.accessToken) return
      handleAuthData(providerAuthData)
      try {
        const { data } =
          await api.get<PermissionsResponseType>(
            ENDPOINTS.PERMISSIONS
          )
        const targetRoute =
          NavigationHelper.getFirstAccessibleRoute(
            data.permissionGroups,
            data.superAdmin
          )
        navigate(targetRoute)
      } catch {
        navigate(FRONTEND_ROUTES.DASHBOARD.href)
      }
    },
    [handleAuthData, navigate]
  )
  const handleSetForgotData = (email: string) => {
    const forgotData = { email }
    StorageHelper.setItem(
      AUTH_LOCAL_STORAGE_KEY_FORGOT,
      stringify(forgotData)
    )
    setForgotData(forgotData)
  }

  const previousWhiteLabelIdRef = useRef<
    string | null | undefined
  >(undefined)

  useEffect(() => {
    const currentWhiteLabelId = selectedWhiteLabel?.uuid
    if (
      previousWhiteLabelIdRef.current ===
      currentWhiteLabelId
    ) {
      return
    }

    previousWhiteLabelIdRef.current = currentWhiteLabelId

    if (currentWhiteLabelId) {
      StorageHelper.setItem(
        AUTH_LOCAL_STORAGE_KEY_WHITELABEL_UUID,
        currentWhiteLabelId
      )
    } else {
      StorageHelper.removeItem(
        AUTH_LOCAL_STORAGE_KEY_WHITELABEL_UUID
      )
    }

    const queryClient = getBrowserQueryClient()
    if (queryClient) {
      queryClient.clear()
    }
  }, [selectedWhiteLabel])

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center text-center">
        <Loader2 size={32} className="animate-spin" />
      </div>
    )
  }
  return (
    <AuthContext.Provider
      value={{
        login: fetchLogin,
        authData,
        selectedWhiteLabel,
        setSelectedWhiteLabel,
        isLoading,
        setIsLoading,
        logout,
        loginWithProvider,
        handleUpdateAuthData,
        handleAuthData,
        forgotData,
        handleSetForgotData,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error(
      "useAuth must be used within an AuthProvider"
    )
  }
  return context
}
